import type { Store } from '../persistence/port';
import { appendAppLog } from '../platform/appLog';
import { alsBase64, baueUebergabeTeile, datenbestand, IST_VIDEO, sammleBelege } from '../platform/beleguebergabe';
import { relaySender } from './anhaenge';
import type { ServerClient } from './client';
import type { MultipartSender } from './multipart';

/**
 * Relays `UPLOAD_LOGS {uploadID, attachments}`: das Log-ZIP an
 * `POST /rest/v1/client-upload/zip?uploadID=…`, Teil `datei`
 * (`RELAY/docs/protokoll/PROTOKOLL-CLIENT-SERVER.md` §7.2).
 *
 * **Inhalt:** immer die Geräte-Logs (`log.txt`, `log.N.gz`, `log-sql.txt`, `log-sql.N.gz`) und
 * `datenbank.json`; mit `attachments` dazu alles unter `logs/` und das Archiv, ohne Videos.
 *
 * **Ausgang:** `'fertig'` heisst quittieren — die Datei liegt bei Relay, oder Relay hat sie
 * abgewiesen und das über `client-upload/failed` erfahren. `'spaeter'` heisst liegen lassen:
 * kein Netz, `5xx`, oder `409 UPLOAD_IN_PROGRESS` (laut Relay „später wiederholen“).
 */
export const UPLOAD_LOGS = 'UPLOAD_LOGS';
export const ZIP_PFAD = '/rest/v1/client-upload/zip';
export const FEHLER_PFAD = '/rest/v1/client-upload/failed';

export type Ausgang = 'fertig' | 'spaeter';

export interface ArchivDeps {
  client: ServerClient;
  basisUrl: string;
  store?: Store;
  uploadID: string;
  attachments: boolean;
  log?: (text: string, stufe?: 'warn') => Promise<void>;
  /** Einschiebbar für Tests: das fertige ZIP als Base64. */
  packe?: () => Promise<string>;
  /** Einschiebbar für Tests: der Transport zum Token. */
  sender?: (token: string) => MultipartSender;
}

export async function ladeProtokolleHoch(deps: ArchivDeps): Promise<Ausgang> {
  const log = deps.log ?? appendAppLog;
  const was = 'Relay: UPLOAD_LOGS ' + deps.uploadID;
  const packe =
    deps.packe ??
    (async () => {
      const zusatz = deps.store ? [{ name: 'datenbank.json', daten: await datenbestand(deps.store) }] : [];
      const { stuecke } = await sammleBelege(baueUebergabeTeile(), {
        zusatz,
        // Die Geräte-Logs aus der Wurzel nimmt `sammleBelege` immer mit; das hier betrifft nur `logs/` und das Archiv.
        auslassen: (pfad) => !deps.attachments || IST_VIDEO.test(pfad),
      });
      return alsBase64(stuecke);
    });

  const meldeFehlschlag = async (error: string): Promise<Ausgang> => {
    try {
      await deps.client.post(FEHLER_PFAD, { uploadID: deps.uploadID, error });
    } catch (fehler) {
      await log(was + ': Fehlschlag nicht meldbar, später erneut — ' + String(fehler), 'warn');
      return 'spaeter';
    }
    await log(was + ' gescheitert, an Relay gemeldet: ' + error, 'warn');
    return 'fertig';
  };

  let base64: string;
  try {
    base64 = await packe();
  } catch (fehler) {
    return meldeFehlschlag('Packen gescheitert: ' + String(fehler));
  }

  try {
    const { token } = await deps.client.anmelden();
    const antwort = await (deps.sender ?? relaySender)(token)({
      url: deps.basisUrl.replace(/\/+$/, '') + ZIP_PFAD + '?uploadID=' + encodeURIComponent(deps.uploadID),
      felder: {},
      datei: { name: 'logs.zip', mime: 'application/zip', base64, feld: 'datei' },
    });
    if (antwort.ok) {
      await log(was + ': ' + String(Math.round((base64.length * 3) / 4)) + ' Bytes übertragen.');
      return 'fertig';
    }
    if (antwort.status === 401) {
      // Abgelaufenes Token: neu anmelden und im nächsten Takt wieder versuchen.
      deps.client.vergessen();
      return 'spaeter';
    }
    if (antwort.status === 409 && antwort.text.includes('UPLOAD_IN_PROGRESS')) return 'spaeter';
    if (antwort.status >= 400 && antwort.status < 500) {
      return meldeFehlschlag('HTTP ' + String(antwort.status) + ' ' + antwort.text.slice(0, 200));
    }
    await log(was + ': HTTP ' + String(antwort.status) + ', später erneut.', 'warn');
    return 'spaeter';
  } catch (fehler) {
    await log(was + ': nicht übertragen, später erneut — ' + String(fehler), 'warn');
    return 'spaeter';
  }
}
